/**
 * Mounted once by __root.tsx.
 *
 * GTM is the sole browser runtime owner of GA4 and the Meta Pixel, so this
 * component no longer initialises any tag and never fires page_view /
 * PageView — GTM owns page views (including SPA history changes).
 *
 * Responsibilities:
 *  1. Consent Mode v2 — apply the visitor's stored choice at boot and forward
 *     subsequent changes as `consent update` commands.
 *  2. Consent capture — persist every accept/decline to the server-side
 *     ledger (`marketing_consent_events`) for audit + suppression.
 */
import { useEffect, useRef } from "react";

import { onConsentChange, type ConsentRecord } from "@/lib/meta/consent";
import {
  applyStoredGoogleConsent,
  updateGoogleConsentCategories,
} from "@/lib/analytics/consent-mode";
import { getConsentState, onConsentStateChange } from "@/lib/consent/store";
import { isEnforcementEnabled } from "@/lib/consent/enforcement";
import { getAnonymousId } from "@/lib/identity/anonymousId";

async function logConsent(record: ConsentRecord) {
  if (record.status !== "granted" && record.status !== "denied") return;
  try {
    const anonymousId = getAnonymousId();
    if (!anonymousId || anonymousId.length < 8) return;
    const { recordMarketingConsent } = await import("@/lib/consent-log.functions");
    await recordMarketingConsent({
      data: {
        anonymousId,
        status: record.status,
        source: record.source || "banner",
        channel: record.channel || "advertising",
        policyVersion: record.policyVersion,
        pageUrl: window.location.origin,
        userAgent: navigator.userAgent.slice(0, 400),
      },
    });
  } catch {
    /* logging must never break the visitor's experience */
  }
}

export function MetaBootstrap() {
  const lastLogged = useRef<string>("");

  // Category-level Consent Mode v2 updates + deferred tag boot. With
  // enforcement on the container is not injected at page load; accepting a
  // category later starts it without a reload, in this tab and any other.
  useEffect(() => {
    const apply = (state: ReturnType<typeof getConsentState>) => {
      updateGoogleConsentCategories(state);
      if (!isEnforcementEnabled()) return;
      if (state.analytics === "granted" && state.advertising === "granted") {
        const boot = (window as unknown as { __truweBootGtm?: () => void }).__truweBootGtm;
        try {
          boot?.();
        } catch {
          /* tag boot must never break the page */
        }
      }
    };
    apply(getConsentState());
    return onConsentStateChange(apply);
  }, []);

  useEffect(() => {
    void applyStoredGoogleConsent();
    return onConsentChange((r) => {
      updateGoogleConsentCategories(getConsentState());

      // De-dupe repeated events for the same choice+timestamp (storage
      // events fire in every open tab).
      const key = `${r.status}:${r.timestamp}`;
      if (lastLogged.current === key) return;
      lastLogged.current = key;
      try {
        if (window.localStorage.getItem("truwe_consent_logged") === key) return;
        window.localStorage.setItem("truwe_consent_logged", key);
      } catch {
        /* private mode — fall back to per-tab de-dupe */
      }
      void logConsent(r);
    });
  }, []);

  return null;
}
